/**
 * Campo de red animado: "flujos de informacion" del ecosistema.
 *
 * Tres capas sobre el mismo canvas:
 *   1. Nodos    -> puntos que derivan lentamente (los sistemas conectados).
 *   2. Enlaces  -> lineas entre nodos cercanos, con opacidad por distancia.
 *   3. Paquetes -> destellos que VIAJAN por esos enlaces, con estela. Esta es
 *      la capa que da la sensacion de informacion circulando, y no de un
 *      simple campo de particulas decorativo.
 *
 * Esta implementacion es intencionadamente independiente de React para poder
 * reutilizarla tal cual en el tema de login de Keycloak (ver
 * infrastructure/keycloak/themes/.../resources/js/network-field.js), de modo
 * que el portal y la pantalla de credenciales se vean como la misma pieza.
 */

export interface NetworkFieldOptions {
  /** Densidad de nodos: pixeles de area por nodo. Mas alto = menos nodos. */
  areaPerNode?: number;
  maxNodes?: number;
  minNodes?: number;
  /** Numero de paquetes viajando simultaneamente. */
  packetCount?: number;
  /** Colores; por defecto se leen de las variables CSS del tema. */
  colors?: Partial<NetworkColors>;
  /**
   * Opacidad maxima de los enlaces. Sobre fondo claro hay que bajarla: una
   * linea oscura sobre blanco pesa mucho mas que una clara sobre negro.
   */
  linkAlpha?: number;
}

interface NetworkColors {
  node: string;
  link: string;
  packet: string;
  packetGlow: string;
  auroraA: string;
  auroraB: string;
}

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  /** Fase para el pulso de brillo, para que no parpadeen todos a la vez. */
  phase: number;
}

interface Packet {
  from: number;
  to: number;
  /** Progreso 0..1 a lo largo del enlace. */
  t: number;
  speed: number;
}

const DEFAULTS = {
  areaPerNode: 17_000,
  maxNodes: 84,
  minNodes: 26,
  packetCount: 16,
  linkAlpha: 0.16,
} as const;

const FALLBACK_COLORS: NetworkColors = {
  // La infraestructura (nodos y enlaces) se mantiene neutra; lo que se tine
  // de rojo corporativo es la INFORMACION que circula. Un campo entero en
  // rojo se leeria como una alarma, no como una marca.
  node: 'rgba(226, 232, 240, 0.45)',
  link: 'rgba(160, 170, 190, 1)',
  packet: 'rgba(255, 90, 92, 1)',
  packetGlow: 'rgba(200, 21, 29, 1)',
  auroraA: 'rgba(200, 21, 29, 0.17)',
  auroraB: 'rgba(120, 12, 18, 0.18)',
};

export interface NetworkFieldHandle {
  destroy: () => void;
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function createNetworkField(
  canvas: HTMLCanvasElement,
  options: NetworkFieldOptions = {},
): NetworkFieldHandle {
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) {
    return { destroy: () => undefined };
  }

  const settings = { ...DEFAULTS, ...options };
  const colors: NetworkColors = { ...FALLBACK_COLORS, ...options.colors };
  const reducedMotion = prefersReducedMotion();

  let width = 0;
  let height = 0;
  let linkDistance = 150;
  let nodes: Node[] = [];
  let packets: Packet[] = [];
  let frame = 0;
  let running = true;
  let pointerX = 0;
  let pointerY = 0;
  let parallaxX = 0;
  let parallaxY = 0;

  /* --------------------------------------------------------------- setup */

  function resize(): void {
    const rect = canvas.getBoundingClientRect();
    // Se limita el DPR a 2: por encima el coste de pintado no compensa.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    width = Math.max(rect.width, 1);
    height = Math.max(rect.height, 1);

    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    context!.setTransform(dpr, 0, 0, dpr, 0, 0);

    // En pantallas estrechas se acorta el alcance de los enlaces para que la
    // malla no se convierta en una mancha solida.
    linkDistance = Math.min(165, Math.max(96, width / 9));

    const target = Math.round((width * height) / settings.areaPerNode);
    const count = Math.max(settings.minNodes, Math.min(settings.maxNodes, target));

    seedNodes(count);
    seedPackets();
  }

  function seedNodes(count: number): void {
    const previous = nodes;
    nodes = [];

    for (let index = 0; index < count; index += 1) {
      const existing = previous[index];
      nodes.push({
        // Al redimensionar se reubican proporcionalmente para que no "salten".
        x: existing ? Math.min(existing.x, width) : Math.random() * width,
        y: existing ? Math.min(existing.y, height) : Math.random() * height,
        vx: existing?.vx ?? (Math.random() - 0.5) * 0.18,
        vy: existing?.vy ?? (Math.random() - 0.5) * 0.18,
        radius: existing?.radius ?? 0.9 + Math.random() * 1.5,
        phase: existing?.phase ?? Math.random() * Math.PI * 2,
      });
    }
  }

  function seedPackets(): void {
    packets = [];
    if (reducedMotion || nodes.length < 2) return;

    for (let index = 0; index < settings.packetCount; index += 1) {
      packets.push({ from: 0, to: 0, t: 1, speed: 0 });
    }
  }

  /** Coloca un paquete en un enlace activo elegido al azar. */
  function respawn(packet: Packet): void {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const from = (Math.random() * nodes.length) | 0;
      const to = (Math.random() * nodes.length) | 0;
      if (from === to) continue;

      const distance = Math.hypot(nodes[from].x - nodes[to].x, nodes[from].y - nodes[to].y);
      if (distance > linkDistance * 0.95) continue;

      packet.from = from;
      packet.to = to;
      packet.t = 0;
      // Los paquetes cortos viajan algo mas rapido: se lee como trafico real.
      packet.speed = (0.0042 + Math.random() * 0.0055) * (linkDistance / Math.max(distance, 24));
      return;
    }
    // Si no se encontro enlace valido se reintenta en el siguiente frame.
    packet.t = 1;
    packet.speed = 0;
  }

  /* -------------------------------------------------------------- dibujo */

  function drawAurora(): void {
    // Dos halos suaves que dan profundidad al fondo plano.
    const a = context!.createRadialGradient(
      width * 0.22 + parallaxX * 2,
      height * 0.18 + parallaxY * 2,
      0,
      width * 0.22,
      height * 0.18,
      Math.max(width, height) * 0.75,
    );
    a.addColorStop(0, colors.auroraA);
    a.addColorStop(1, 'transparent');
    context!.fillStyle = a;
    context!.fillRect(0, 0, width, height);

    const b = context!.createRadialGradient(
      width * 0.82 - parallaxX * 2,
      height * 0.86 - parallaxY * 2,
      0,
      width * 0.82,
      height * 0.86,
      Math.max(width, height) * 0.6,
    );
    b.addColorStop(0, colors.auroraB);
    b.addColorStop(1, 'transparent');
    context!.fillStyle = b;
    context!.fillRect(0, 0, width, height);
  }

  function withAlpha(color: string, alpha: number): string {
    // Acepta rgba(...) o rgb(...) y sustituye el canal alfa.
    const match = /^rgba?\(([^)]+)\)$/.exec(color);
    if (!match) return color;
    const parts = match[1].split(',').map((part) => part.trim());
    const [r, g, b] = parts;
    const base = parts.length > 3 ? Number.parseFloat(parts[3]) : 1;
    return `rgba(${r}, ${g}, ${b}, ${(alpha * base).toFixed(3)})`;
  }

  function drawLinks(): void {
    context!.lineWidth = 1;

    for (let i = 0; i < nodes.length; i += 1) {
      for (let j = i + 1; j < nodes.length; j += 1) {
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const distance = Math.hypot(dx, dy);
        if (distance > linkDistance) continue;

        // Cuanto mas cerca, mas visible. Tope bajo para que sea atmosfera.
        const alpha = (1 - distance / linkDistance) * settings.linkAlpha;
        context!.strokeStyle = withAlpha(colors.link, alpha);
        context!.beginPath();
        context!.moveTo(nodes[i].x, nodes[i].y);
        context!.lineTo(nodes[j].x, nodes[j].y);
        context!.stroke();
      }
    }
  }

  function drawNodes(): void {
    for (const node of nodes) {
      const pulse = reducedMotion ? 0.7 : 0.55 + Math.sin(frame * 0.012 + node.phase) * 0.25;
      context!.fillStyle = withAlpha(colors.node, pulse);
      context!.beginPath();
      context!.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      context!.fill();
    }
  }

  function drawPackets(): void {
    for (const packet of packets) {
      if (packet.t >= 1) continue;

      const from = nodes[packet.from];
      const to = nodes[packet.to];
      if (!from || !to) continue;

      const x = from.x + (to.x - from.x) * packet.t;
      const y = from.y + (to.y - from.y) * packet.t;

      // Estela: un tramo corto por detras del paquete.
      const trail = Math.max(0, packet.t - 0.16);
      const tx = from.x + (to.x - from.x) * trail;
      const ty = from.y + (to.y - from.y) * trail;

      const gradient = context!.createLinearGradient(tx, ty, x, y);
      gradient.addColorStop(0, withAlpha(colors.packet, 0));
      gradient.addColorStop(1, withAlpha(colors.packet, 0.75));
      context!.strokeStyle = gradient;
      context!.lineWidth = 1.6;
      context!.beginPath();
      context!.moveTo(tx, ty);
      context!.lineTo(x, y);
      context!.stroke();

      // Cabeza con halo.
      context!.shadowBlur = 10;
      context!.shadowColor = colors.packetGlow;
      context!.fillStyle = withAlpha(colors.packet, 0.95);
      context!.beginPath();
      context!.arc(x, y, 1.9, 0, Math.PI * 2);
      context!.fill();
      context!.shadowBlur = 0;
    }
  }

  /* --------------------------------------------------------------- ciclo */

  function step(): void {
    frame += 1;

    // Parallax con amortiguacion: sigue al puntero sin dar tirones.
    parallaxX += (pointerX - parallaxX) * 0.04;
    parallaxY += (pointerY - parallaxY) * 0.04;

    for (const node of nodes) {
      node.x += node.vx;
      node.y += node.vy;

      // Rebote suave en los bordes.
      if (node.x < 0 || node.x > width) node.vx *= -1;
      if (node.y < 0 || node.y > height) node.vy *= -1;
      node.x = Math.max(0, Math.min(width, node.x));
      node.y = Math.max(0, Math.min(height, node.y));
    }

    for (const packet of packets) {
      if (packet.t >= 1) {
        respawn(packet);
        continue;
      }
      packet.t += packet.speed;
    }
  }

  function render(): void {
    context!.clearRect(0, 0, width, height);
    drawAurora();

    context!.save();
    context!.translate(parallaxX, parallaxY);
    drawLinks();
    drawNodes();
    drawPackets();
    context!.restore();
  }

  let animationId = 0;

  function loop(): void {
    if (!running) return;
    step();
    render();
    animationId = window.requestAnimationFrame(loop);
  }

  /* ------------------------------------------------------------ eventos */

  function onPointerMove(event: PointerEvent): void {
    if (reducedMotion) return;
    const rect = canvas.getBoundingClientRect();
    // Desplazamiento maximo de 12px: sugerencia de profundidad, no un efecto.
    pointerX = ((event.clientX - rect.left) / rect.width - 0.5) * -24;
    pointerY = ((event.clientY - rect.top) / rect.height - 0.5) * -12;
  }

  function onVisibilityChange(): void {
    // No se gasta CPU con la pestana en segundo plano.
    if (document.hidden) {
      running = false;
      window.cancelAnimationFrame(animationId);
    } else if (!reducedMotion) {
      running = true;
      loop();
    }
  }

  const resizeObserver =
    typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => resize()) : null;

  resize();

  if (reducedMotion) {
    // Sin animacion: un unico fotograma estatico, que sigue siendo bonito.
    render();
  } else {
    loop();
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  resizeObserver?.observe(canvas);
  if (!resizeObserver) window.addEventListener('resize', resize);

  return {
    destroy: () => {
      running = false;
      window.cancelAnimationFrame(animationId);
      window.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', resize);
    },
  };
}
